import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { ownerApi, type DoctoralMonitorItem } from '@/features/workspace/api';
import { routes } from '@/lib/routes';
import '@/styles/advisor-monitor.css';

type Tab = 'overview' | 'years' | 'supervisors' | 'registry';
type DoctoralMonitorRow = DoctoralMonitorItem;

const COLORS = ['#41d2ff', '#49ddb7', '#e2ad3a', '#9e80ff', '#ff7664'];

function initials(name: string): string {
  return (
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((x) => x[0] || '')
      .join('')
      .toUpperCase() || '—'
  );
}

function pctSeed(row: DoctoralMonitorRow): number {
  let h = 0;
  for (const c of row.name) h = (h * 31 + c.charCodeAt(0)) % 997;
  return 34 + (h % 64);
}

function courseLabel(row: DoctoralMonitorRow): string {
  if (row.defenseStatus === 'Защищён') return 'выпускник';
  const offset = 2028 - row.completionYear;
  if (offset >= 2) return '1 курс';
  if (offset === 1) return '2 курс';
  return '3 курс';
}

/** Template ADVISOR_MONITORING — ACADEMIA · ADVISOR CONTROL. */
export const OwnerDoctoralMonitoringPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('overview');
  const [rows, setRows] = useState<DoctoralMonitorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const years = useMemo(() => {
    if (!rows.length) return { min: 2020, max: 2028 };
    const ys = rows.map((r) => r.completionYear);
    return { min: Math.min(...ys), max: Math.max(...ys) };
  }, [rows]);
  const [fromYear, setFromYear] = useState(years.min);
  const [toYear, setToYear] = useState(years.max);
  const [supervisor, setSupervisor] = useState('all');
  const [program, setProgram] = useState('all');
  const [defense, setDefense] = useState('all');
  const [academic, setAcademic] = useState('all');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<DoctoralMonitorRow | null>(null);

  useEffect(() => {
    let cancelled = false;
    ownerApi
      .listDoctoralMonitor()
      .then((items) => {
        if (cancelled) return;
        setRows(items);
        if (items.length) {
          const ys = items.map((r) => r.completionYear);
          setFromYear(Math.min(...ys));
          setToYear(Math.max(...ys));
        }
      })
      .catch(() => {
        if (!cancelled) setLoadError('Не удалось загрузить реестр докторантов');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const total = rows.length;

  const supervisors = useMemo(
    () =>
      [...new Set(rows.map((r) => r.supervisor).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, 'ru'),
      ),
    [rows],
  );
  const programs = useMemo(
    () => [...new Set(rows.map((r) => r.program))].sort((a, b) => a.localeCompare(b, 'ru')),
    [rows],
  );
  const academics = useMemo(() => {
    const set = new Set(rows.map((r) => r.academic).filter((x) => Boolean(x) && x !== '—'));
    return [...set].sort((a, b) => a.localeCompare(b, 'ru'));
  }, [rows]);

  const filtered = useMemo(() => {
    return rows.filter((row) => {
      if (row.completionYear < fromYear || row.completionYear > toYear) return false;
      if (supervisor !== 'all' && row.supervisor !== supervisor) return false;
      if (program !== 'all' && row.program !== program) return false;
      if (defense !== 'all' && row.defenseStatus !== defense) return false;
      const rowAcademic = row.academic || '—';
      if (academic !== 'all' && rowAcademic !== academic) return false;
      if (query.trim()) {
        const q = query.trim().toLowerCase();
        const email = row.email || '';
        if (
          !`${row.name} ${row.supervisor} ${row.program} ${email}`
            .toLowerCase()
            .includes(q)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [rows, fromYear, toYear, supervisor, program, defense, academic, query]);

  const defended = filtered.filter((r) => r.defenseStatus === 'Защищён').length;
  const notDefended = filtered.length - defended;

  const byYear = useMemo(() => {
    const map: Record<number, number> = {};
    filtered.forEach((row) => {
      map[row.completionYear] = (map[row.completionYear] || 0) + 1;
    });
    return Object.entries(map)
      .map(([year, count]) => ({ year: Number(year), count }))
      .sort((a, b) => a.year - b.year);
  }, [filtered]);

  const bySupervisor = useMemo(() => {
    const map: Record<string, DoctoralMonitorRow[]> = {};
    filtered.forEach((row) => {
      const key = row.supervisor || 'Не указан';
      map[key] = map[key] || [];
      map[key].push(row);
    });
    return Object.entries(map)
      .map(([name, rows]) => ({
        name,
        count: rows.length,
        defended: rows.filter((r) => r.defenseStatus === 'Защищён').length,
      }))
      .sort((a, b) => b.count - a.count);
  }, [filtered]);

  const maxBar = Math.max(
    1,
    ...byYear.map((item) => item.count),
    ...bySupervisor.map((item) => item.count),
  );

  const recent = filtered.slice(-12).reverse();

  useEffect(() => {
    document.body.classList.add('advisor-monitor-lock');
    return () => {
      document.body.classList.remove('advisor-monitor-lock');
    };
  }, []);

  const close = () => navigate(routes.owner);

  const resetFilters = () => {
    setFromYear(years.min);
    setToYear(years.max);
    setSupervisor('all');
    setProgram('all');
    setDefense('all');
    setAcademic('all');
    setQuery('');
  };

  const exportCsv = () => {
    const header = ['ФИО', 'Руководитель', 'Программа', 'Год', 'Защита', 'Дата'];
    const lines = filtered.map((r) =>
      [r.name, r.supervisor, r.program, r.completionYear, r.defenseStatus, r.defenseDate || '—']
        .map((c) => `"${String(c).replace(/"/g, '""')}"`)
        .join(','),
    );
    const blob = new Blob([[header.join(','), ...lines].join('\n')], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'doctoral-monitoring.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="advisor-monitor-overlay advisor-monitor-root"
      role="dialog"
      aria-modal="true"
      aria-label="Мониторинг докторантов"
      style={{ ['--line' as string]: 'rgba(255,255,255,.09)' }}
    >
      <div className="advisor-monitor-shell">
        <header className="advisor-monitor-top">
          <button type="button" className="advisor-monitor-back" onClick={close} aria-label="Назад">
            ←
          </button>
          <div>
            <small>Контроль работы эдвайзеров</small>
            <h1>Мониторинг докторантов</h1>
            <p>
              {loading ? 'Загрузка реестра…' : loadError || `${total} записей · обучение и защиты`}
            </p>
          </div>
          <button type="button" className="advisor-monitor-close" onClick={close} aria-label="Закрыть">
            ×
          </button>
        </header>

        <main className="advisor-monitor-main">
          {selected ? (
            <section className="advisor-monitor-panel">
              <header>
                <div>
                  <h3>{selected.name}</h3>
                  <p>
                    {courseLabel(selected)} · {selected.program.replace(/[«»]/g, '')}
                  </p>
                </div>
                <button type="button" className="advisor-monitor-btn" onClick={() => setSelected(null)}>
                  ← К выборке
                </button>
              </header>
              <div className="advisor-monitor-body advisor-monitor-student-detail">
                <aside className="advisor-monitor-detail-side">
                  <div className="advisor-monitor-avatar" style={{ width: 56, height: 56, fontSize: 16 }}>
                    {initials(selected.name)}
                  </div>
                  <h3 style={{ marginTop: 12 }}>{selected.name}</h3>
                  <p>
                    Научный руководитель: {selected.supervisor || '—'}
                    <br />
                    Год завершения: {selected.completionYear}
                    <br />
                    Защита: {selected.defenseStatus}
                    {selected.defenseDate ? ` · ${selected.defenseDate}` : ''}
                    <br />
                    Досье: {pctSeed(selected)}%
                  </p>
                </aside>
                <div>
                  <div className="advisor-monitor-notice">
                    Карточка открыта из единого реестра мониторинга. Пустые поля исходных данных
                    показываются как «—» и не заполняются автоматически.
                  </div>
                  <div className="advisor-monitor-actions" style={{ marginTop: 12 }}>
                    <button type="button" className="advisor-monitor-btn gold" onClick={exportCsv}>
                      Экспорт строки
                    </button>
                  </div>
                </div>
              </div>
            </section>
          ) : (
            <>
              <section className="advisor-monitor-hero" style={{ ['--a' as string]: '#41d2ff' }}>
                <div>
                  <small>
                    {total} ЗАПИСЕЙ · 2 НАПРАВЛЕНИЯ · GLOBAL SCOPE
                  </small>
                  <h2>Мониторинг докторантов</h2>
                  <p>
                    Один общий набор данных и один экран для эдвайзера, руководителя подразделения,
                    лаборанта и владельца. Отсутствующие в исходных файлах и профиле поля показываются
                    «—» и не генерируются.
                  </p>
                </div>
                <div className="advisor-monitor-actions">
                  <button type="button" className="advisor-monitor-btn" onClick={resetFilters}>
                    Сбросить
                  </button>
                  <button type="button" className="advisor-monitor-btn gold" onClick={exportCsv}>
                    Экспорт CSV
                  </button>
                </div>
              </section>

              <div className="advisor-monitor-kpis">
                <article className="advisor-monitor-kpi" style={{ ['--a' as string]: '#41d2ff' }}>
                  <small>В выборке</small>
                  <b>{filtered.length}</b>
                  <span>из {total} исходных записей</span>
                </article>
                <article className="advisor-monitor-kpi" style={{ ['--a' as string]: '#49ddb7' }}>
                  <small>Защищено</small>
                  <b>{defended}</b>
                  <span>подтверждено исходной датой/статусом</span>
                </article>
                <article className="advisor-monitor-kpi" style={{ ['--a' as string]: '#e2ad3a' }}>
                  <small>Не защищено</small>
                  <b>{notDefended}</b>
                  <span>нет зафиксированной защиты</span>
                </article>
                <article className="advisor-monitor-kpi" style={{ ['--a' as string]: '#9e80ff' }}>
                  <small>Активный период</small>
                  <b>
                    {fromYear}—{toYear}
                  </b>
                  <span>
                    границы dataset {years.min}—{years.max}
                  </span>
                </article>
                <article className="advisor-monitor-kpi" style={{ ['--a' as string]: '#ff7664' }}>
                  <small>Руководители</small>
                  <b>{new Set(filtered.map((r) => r.supervisor)).size}</b>
                  <span>в текущей выборке</span>
                </article>
              </div>

              <div className="advisor-monitor-filters">
                <label className="advisor-monitor-field advisor-monitor-range">
                  <span>ПЕРИОД ЗАВЕРШЕНИЯ</span>
                  <div className="advisor-monitor-range-row">
                    <input
                      type="range"
                      min={years.min}
                      max={years.max}
                      value={fromYear}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setFromYear(Math.min(v, toYear));
                      }}
                    />
                    <b>
                      {fromYear}—{toYear}
                    </b>
                    <input
                      type="range"
                      min={years.min}
                      max={years.max}
                      value={toYear}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setToYear(Math.max(v, fromYear));
                      }}
                    />
                  </div>
                </label>
                <label className="advisor-monitor-field">
                  <span>НАУЧНЫЙ РУКОВОДИТЕЛЬ</span>
                  <select value={supervisor} onChange={(e) => setSupervisor(e.target.value)}>
                    <option value="all">Все руководители</option>
                    {supervisors.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="advisor-monitor-field">
                  <span>ПРОГРАММА</span>
                  <select value={program} onChange={(e) => setProgram(e.target.value)}>
                    <option value="all">Все программы</option>
                    {programs.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="advisor-monitor-field">
                  <span>СТАТУС ЗАЩИТЫ</span>
                  <select value={defense} onChange={(e) => setDefense(e.target.value)}>
                    <option value="all">Все</option>
                    <option value="Защищён">Защищён</option>
                    <option value="Не защищён">Не защищён</option>
                  </select>
                </label>
                <label className="advisor-monitor-field">
                  <span>АКАДЕМИЧЕСКИЙ СТАТУС</span>
                  <select value={academic} onChange={(e) => setAcademic(e.target.value)}>
                    <option value="all">Все</option>
                    {academics.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="advisor-monitor-field">
                  <span>ПОИСК</span>
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="ФИО / email / программа / руководитель"
                  />
                </label>
              </div>

              <div className="advisor-monitor-tabs">
                {(
                  [
                    ['overview', 'Обзор'],
                    ['years', 'По годам'],
                    ['supervisors', 'По руководителям'],
                    ['registry', 'Полный реестр'],
                  ] as const
                ).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    className={tab === id ? 'active' : ''}
                    onClick={() => setTab(id)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {tab === 'overview' ? (
                <>
                  <div className="advisor-monitor-grid">
                    <article
                      className="advisor-monitor-card span6"
                      style={{ ['--a' as string]: '#49ddb7' }}
                    >
                      <h4>Защитившиеся</h4>
                      <p>
                        {defended} записей. Дата защиты хранится в реестре и видна в полном списке.
                      </p>
                      <footer>
                        <span className="advisor-monitor-chip good">
                          {Math.round((defended / Math.max(1, filtered.length)) * 100)}% выборки
                        </span>
                      </footer>
                    </article>
                    <article
                      className="advisor-monitor-card span6"
                      style={{ ['--a' as string]: '#ff7664' }}
                    >
                      <h4>Без зафиксированной защиты</h4>
                      <p>
                        {notDefended} записей: среди них есть действующие докторанты и выпускники
                        прошлых лет.
                      </p>
                      <footer>
                        <span className="advisor-monitor-chip bad">нужен контроль статуса</span>
                      </footer>
                    </article>
                  </div>

                  <section className="advisor-monitor-panel">
                    <header>
                      <div>
                        <h3>Последние записи выборки</h3>
                        <p>Быстрый просмотр; для всех полей откройте «Полный реестр».</p>
                      </div>
                    </header>
                    <div className="advisor-monitor-body">
                      <div className="advisor-monitor-student-grid">
                        {recent.map((row) => (
                          <button
                            key={`${row.name}-${row.completionYear}-${row.program}`}
                            type="button"
                            className="advisor-monitor-student"
                            onClick={() => setSelected(row)}
                          >
                            <span className="advisor-monitor-avatar">{initials(row.name)}</span>
                            <span>
                              <strong>{row.name}</strong>
                              <small>
                                {courseLabel(row)} · {row.program.replace(/[«»]/g, '')}
                                <br />
                                {row.supervisor || 'Руководитель не указан'} · досье {pctSeed(row)}%
                              </small>
                            </span>
                            <em>›</em>
                          </button>
                        ))}
                        {!recent.length ? (
                          <div className="advisor-monitor-empty">По заданным условиям записей нет.</div>
                        ) : null}
                      </div>
                    </div>
                  </section>
                </>
              ) : null}

              {tab === 'years' ? (
                <section className="advisor-monitor-panel">
                  <header>
                    <div>
                      <h3>Динамика по годам завершения</h3>
                      <p>
                        {years.min}–{years.max}; выбранный диапазон меняется ползунками выше.
                      </p>
                    </div>
                  </header>
                  <div className="advisor-monitor-body advisor-monitor-bars">
                    {byYear.map((item, index) => (
                      <div key={item.year} className="advisor-monitor-bar">
                        <button
                          type="button"
                          onClick={() => {
                            setFromYear(item.year);
                            setToYear(item.year);
                            setTab('registry');
                          }}
                        >
                          {item.year}
                        </button>
                        <div className="advisor-monitor-bar-track">
                          <i
                            style={
                              {
                                '--w': `${(item.count / maxBar) * 100}%`,
                                '--c': COLORS[index % COLORS.length],
                              } as CSSProperties
                            }
                          />
                        </div>
                        <b>{item.count}</b>
                      </div>
                    ))}
                    {!byYear.length ? (
                      <div className="advisor-monitor-empty">Нет данных по фильтру</div>
                    ) : null}
                  </div>
                </section>
              ) : null}

              {tab === 'supervisors' ? (
                <section className="advisor-monitor-panel">
                  <header>
                    <div>
                      <h3>Нагрузка и результаты по научным руководителям</h3>
                      <p>
                        Нажмите на руководителя — откроется его полный список, где сразу видно, кто
                        защитился, а у кого защита ещё не зафиксирована.
                      </p>
                    </div>
                  </header>
                  <div className="advisor-monitor-body advisor-monitor-bars">
                    {bySupervisor.map((item, index) => (
                      <div key={item.name} className="advisor-monitor-bar">
                        <button
                          type="button"
                          onClick={() => {
                            setSupervisor(item.name);
                            setTab('registry');
                          }}
                        >
                          {item.name}
                          <small style={{ display: 'block', color: '#789eaf', marginTop: 2 }}>
                            защищено {item.defended} · не защищено {item.count - item.defended}
                          </small>
                        </button>
                        <div className="advisor-monitor-bar-track">
                          <i
                            style={
                              {
                                '--w': `${(item.count / maxBar) * 100}%`,
                                '--c': COLORS[index % COLORS.length],
                              } as CSSProperties
                            }
                          />
                        </div>
                        <b>{item.count}</b>
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}

              {tab === 'registry' ? (
                <section className="advisor-monitor-panel">
                  <header>
                    <div>
                      <h3>Полный реестр</h3>
                      <p>
                        {total} исходных записей. Пустые поля остаются «—». Текущая выборка ·{' '}
                        {filtered.length}
                      </p>
                    </div>
                  </header>
                  <div className="advisor-monitor-table-wrap">
                    <table className="advisor-monitor-table">
                      <thead>
                        <tr>
                          <th>ФИО</th>
                          <th>Программа</th>
                          <th>Руководитель</th>
                          <th>Год</th>
                          <th>Защита</th>
                          <th>Дата</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((row, index) => (
                          <tr
                            key={`${row.name}-${row.program}-${index}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setSelected(row)}
                          >
                            <td>
                              <strong>{row.name}</strong>
                            </td>
                            <td>{row.program}</td>
                            <td>{row.supervisor || '—'}</td>
                            <td>{row.completionYear}</td>
                            <td>
                              <span
                                className={`advisor-monitor-chip ${
                                  row.defenseStatus === 'Защищён' ? 'good' : 'bad'
                                }`}
                              >
                                {row.defenseStatus}
                              </span>
                            </td>
                            <td>{row.defenseDate || '—'}</td>
                          </tr>
                        ))}
                        {!filtered.length ? (
                          <tr>
                            <td colSpan={6}>
                              <div className="advisor-monitor-empty">
                                По заданным условиям записей нет.
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </section>
              ) : null}
            </>
          )}
        </main>
      </div>
    </div>
  );
};
